import { HiChevronDown } from "react-icons/hi";
import { useRef, useState } from "react";
import { useNettingAccounts } from "@repo/api";
import { useTranslation } from "@repo/core";
import {
  useFloating,
  useClick,
  useDismiss,
  useRole,
  useListNavigation,
  useTypeahead,
  useInteractions,
  autoUpdate,
  offset,
  flip,
  shift,
  size,
  FloatingPortal,
} from "@floating-ui/react";

interface AccountSelectorProps {
  selectedAccountId: string;
  onAccountSelect: (accountId: string) => void;
  isDropdownOpen: boolean;
  setIsDropdownOpen: (open: boolean) => void;
  disabled?: boolean;
}

/** The modal this lives in renders its body in an `overflow-y-auto` container.
 * An absolutely positioned dropdown still counts towards that container's
 * scrollable area, so opening the list used to grow the modal's scroll height,
 * pop up a scrollbar and clip the options. Rendering the list in a portal takes
 * it out of the scroll container entirely: the modal's layout no longer reacts
 * to the dropdown at all, and the list can overhang the modal's edge.
 *
 * Above flowbite's `z-50` modal, matching the tooltip in IndicatorsTable. */
const DROPDOWN_Z_INDEX = 9999;

/** Tailwind's `max-h-60`, kept as the cap when there is plenty of room. */
const MAX_DROPDOWN_HEIGHT = 240;

export const AccountSelector = ({
  selectedAccountId,
  onAccountSelect,
  isDropdownOpen,
  setIsDropdownOpen,
  disabled = false,
}: AccountSelectorProps) => {
  const { tStrategy } = useTranslation();
  const nettingAccountsQuery = useNettingAccounts(undefined, {
    enabled: true,
  });

  const nettingAccounts = nettingAccountsQuery.data || [];
  const selectedIndex = nettingAccounts.findIndex(
    (account) => account.id === selectedAccountId
  );
  const selectedAccount =
    selectedIndex >= 0 ? nettingAccounts[selectedIndex] : undefined;

  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  // Populated by the option refs below; `useListNavigation` reads it to know
  // what it is navigating over. `labelsRef` backs type-to-select.
  const listRef = useRef<Array<HTMLElement | null>>([]);
  const labelsRef = useRef<Array<string | null>>([]);

  // Open state stays owned by the parent modal, which also closes the dropdown
  // when the user navigates between modal pages.
  const { refs, floatingStyles, context } = useFloating({
    open: isDropdownOpen,
    onOpenChange: setIsDropdownOpen,
    placement: "bottom-start",
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(4),
      // Flip above the trigger when the list would not fit below it.
      flip({ padding: 8 }),
      shift({ padding: 8 }),
      size({
        padding: 8,
        apply({ rects, availableHeight, elements }) {
          Object.assign(elements.floating.style, {
            // Match the trigger, the way `w-full` did before the portal.
            width: `${rects.reference.width}px`,
            maxHeight: `${Math.min(availableHeight, MAX_DROPDOWN_HEIGHT)}px`,
          });
        },
      }),
    ],
  });

  const click = useClick(context, { enabled: !disabled });
  // Replaces the previous manual mousedown listener, which compared the event
  // target against the wrapper element. That test breaks once the list is
  // portalled - every option would count as an outside click and close the
  // dropdown before its onClick could fire. useDismiss knows about the
  // floating element wherever it is rendered.
  const dismiss = useDismiss(context);
  // "select" (not "listbox") is the combobox-style trigger + listbox popup
  // pairing this is. It also matters for virtual focus: useRole only hands the
  // active option the `id` that `aria-activedescendant` points at for the
  // "select" and "combobox" roles - under "listbox" it returns no item props at
  // all and the attribute would dangle.
  const role = useRole(context, { role: "select" });

  // `virtual` keeps DOM focus on the trigger and points at the highlighted
  // option with `aria-activedescendant`. That matters here: the modal wraps its
  // content in a `FloatingFocusManager` with `modal={true}`, so moving real
  // focus into a portal rendered outside the modal would fight the focus trap
  // and leave focus on `<body>` once the list unmounts. With virtual focus the
  // caret never leaves the modal.
  const listNavigation = useListNavigation(context, {
    listRef,
    activeIndex,
    selectedIndex: selectedIndex >= 0 ? selectedIndex : null,
    onNavigate: setActiveIndex,
    loop: true,
    virtual: true,
  });

  // Type an account name to jump to it, as with a native <select>. Account
  // labels contain spaces, so while a search is in progress Space belongs to
  // the query rather than to "commit the highlighted option".
  const isTypingRef = useRef(false);
  const typeahead = useTypeahead(context, {
    listRef: labelsRef,
    activeIndex,
    selectedIndex: selectedIndex >= 0 ? selectedIndex : null,
    onMatch: setActiveIndex,
    onTypingChange: (isTyping) => {
      isTypingRef.current = isTyping;
    },
    enabled: isDropdownOpen,
  });

  const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions(
    [click, dismiss, role, listNavigation, typeahead]
  );

  const handleSelect = (accountId: string) => {
    onAccountSelect(accountId);
    setIsDropdownOpen(false);
    setActiveIndex(null);
  };

  return (
    <>
      <button
        ref={refs.setReference}
        disabled={disabled}
        {...getReferenceProps({
          onKeyDown(event) {
            // The trigger keeps focus under virtual navigation, so Enter/Space
            // would otherwise fire the button's own click and just toggle the
            // list. Commit the highlighted option instead - preventDefault also
            // stops the browser synthesising that click.
            if (!isDropdownOpen || activeIndex === null) return;
            const isCommitKey =
              event.key === "Enter" ||
              (event.key === " " && !isTypingRef.current);
            if (!isCommitKey) return;

            const account = nettingAccounts[activeIndex];
            if (!account) return;

            event.preventDefault();
            handleSelect(account.id);
          },
        })}
        className={`w-full flex items-center justify-between px-4 py-2 bg-white border border-gray-300 rounded-lg text-left focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
          disabled ? "opacity-50 cursor-not-allowed" : "hover:bg-gray-50"
        }`}
      >
        <span className="text-gray-900">
          {selectedAccount
            ? `${selectedAccount.name} (${selectedAccount.id})`
            : tStrategy("selectAnAccount")}
        </span>
        <HiChevronDown
          className={`w-5 h-5 text-gray-400 transition-transform ${
            isDropdownOpen ? "transform rotate-180" : ""
          }`}
        />
      </button>

      {isDropdownOpen && (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            style={{ ...floatingStyles, zIndex: DROPDOWN_Z_INDEX }}
            {...getFloatingProps()}
            className="overflow-auto bg-white border border-gray-300 rounded-lg shadow-lg"
          >
            {nettingAccounts.length > 0 ? (
              nettingAccounts.map((account, index) => {
                const label = `${account.name} (${account.id})`;
                const isSelected = selectedAccountId === account.id;
                const isActive = activeIndex === index;

                return (
                  <div
                    key={account.id}
                    // Rendered as a div, not a button: under virtual focus the
                    // options are never focused themselves, and a focusable
                    // child inside the portal would be a tab stop outside the
                    // modal's focus trap.
                    ref={(node) => {
                      listRef.current[index] = node;
                      labelsRef.current[index] = label;
                    }}
                    // `active`/`selected` are consumed by useRole, which turns
                    // them into role="option", aria-selected and the id that
                    // aria-activedescendant references - they are not forwarded
                    // to the DOM as attributes.
                    {...getItemProps({
                      active: isActive,
                      selected: isSelected,
                      onClick: () => handleSelect(account.id),
                    })}
                    // Resolved as one exclusive choice rather than stacking a
                    // hover/active background on top of the selected one -
                    // between two same-specificity `bg-*` utilities the winner
                    // is decided by their order in the generated stylesheet,
                    // not by the order written here.
                    className={`px-4 py-2 text-left cursor-pointer transition-colors ${
                      isActive && isSelected
                        ? "bg-blue-100 text-blue-700"
                        : isActive
                          ? "bg-gray-100 text-gray-900"
                          : isSelected
                            ? "bg-blue-50 text-blue-700"
                            : "text-gray-900"
                    }`}
                  >
                    {label}
                  </div>
                );
              })
            ) : (
              <div className="px-4 py-2 text-gray-500">
                {tStrategy("noAccountsAvailable")}
              </div>
            )}
          </div>
        </FloatingPortal>
      )}
    </>
  );
};
